import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { MapPin } from "@phosphor-icons/react";

const STATEMENTS = [
    { id: "address", label: "I confirm that the address shown above is my current residential address." },
    { id: "citizen", label: "I am a citizen of the United States of America." },
    { id: "not-voted", label: "I have not already voted in this election." },
    { id: "no-bribe", label: "I have not received or been promised anything of value in exchange for my vote." },
];

export default function EligibilityAffirmationDialog({ open, election, address, onProceed, onExit }) {
    const [checks, setChecks] = useState({});

    // Reset checkboxes whenever the dialog re-opens for a new election.
    useEffect(() => { if (open) setChecks({}); }, [open, election?.id]);

    const allChecked = STATEMENTS.every((s) => checks[s.id]);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onExit?.()}>
            <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="eligibility-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-3xl">Voter Eligibility Affirmation</DialogTitle>
                    <DialogDescription className="text-base">
                        Please confirm the following statements before proceeding to vote in:{" "}
                        <span className="font-semibold text-slate-900">{election?.title || "this election"}</span>
                    </DialogDescription>
                </DialogHeader>

                {/* Address callout */}
                <div className="rounded-md border border-civic-200 bg-civic-50 border-l-4 border-l-civic-600 p-5" data-testid="eligibility-address">
                    <div className="flex items-center gap-2 text-sm text-civic-700 font-semibold mb-2">
                        <MapPin size={16} weight="duotone" /> Your Registered Address:
                    </div>
                    <div className="text-civic-800 font-semibold text-lg">
                        {address || <span className="italic font-normal text-slate-500">No address on file</span>}
                    </div>
                </div>

                {/* Statements */}
                <div className="space-y-3" data-testid="eligibility-statements">
                    {STATEMENTS.map((s) => (
                        <label
                            key={s.id}
                            htmlFor={`elig-${s.id}`}
                            className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer transition-colors ${
                                checks[s.id] ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:border-civic-300 bg-white"
                            }`}
                            data-testid={`eligibility-row-${s.id}`}
                        >
                            <Checkbox
                                id={`elig-${s.id}`}
                                checked={!!checks[s.id]}
                                onCheckedChange={(v) => setChecks((c) => ({ ...c, [s.id]: !!v }))}
                                data-testid={`eligibility-check-${s.id}`}
                            />
                            <span className="text-slate-900 leading-relaxed">{s.label}</span>
                        </label>
                    ))}
                </div>

                <Button
                    type="button"
                    onClick={onProceed}
                    disabled={!allChecked}
                    className="mt-2 w-full h-14 text-base bg-civic-600 hover:bg-civic-700 text-white disabled:bg-slate-300 disabled:text-slate-500"
                    data-testid="eligibility-proceed-btn"
                >
                    Confirm Eligibility and Proceed to Ballot
                </Button>
            </DialogContent>
        </Dialog>
    );
}
